<script lang="ts" setup>
import { computed, nextTick, ref, watch } from 'vue';
import CountryFlag from 'vue-country-flag-next';
import { directive as clickAway } from 'vue3-click-away';
import Typography from '@components/Typography.vue';
import { PhoneNumber, type CountryPhoneOption } from '@tools/phoneNumber';

type ClassMap = {
  [key: string]: boolean;
};

interface Props {
  modelValue: PhoneNumber,
  inputName?: string,
  classes?: ClassMap,
  disabled?: boolean,
}

const {
  modelValue,
  inputName = 'phoneNumber',
  classes: classesProp = {} as ClassMap,
  disabled = false,
} = defineProps<Props>();

defineOptions({ directives: { clickAway } });

const finalClasses = computed<ClassMap>(() => ({
  ...classesProp,
  disabled: disabled ?? false,
}));

const inputElement = ref<HTMLInputElement>();
const inputValue = ref<string>(modelValue.inputFormatted ?? '');

const countriesOptions = PhoneNumber.getCountryPhoneOptions();
const countryOption = ref<CountryPhoneOption>(countriesOptions[modelValue.country]);
const showCountrySelection = ref<boolean>(false);

const emit = defineEmits<{(e: 'update:modelValue', payload: PhoneNumber): void;
  (e: 'blur'): void;
  (e: 'focus'): void;
}>();

const phoneNumber = computed<PhoneNumber>({
  get: () => modelValue,
  set: (value: PhoneNumber) => emit('update:modelValue', value),
});

const placeholder = computed<string | undefined>(() => {
  if (countryOption.value.code === 'FR') {
    return '06 12 34 56 78';
  }
  return undefined;
});

watch(inputValue, async (value) => {
  const newPhoneNumber = value.startsWith('+')
    ? new PhoneNumber(value)
    : new PhoneNumber(value, countryOption.value.code);
  countryOption.value = countriesOptions[newPhoneNumber.country];
  inputValue.value = newPhoneNumber.inputFormatted ?? newPhoneNumber.fixed;
  phoneNumber.value = newPhoneNumber;
  await nextTick();
  showCountrySelection.value = false;
});

const closeCountrySelection = (): void => {
  showCountrySelection.value = false;
  inputElement.value!.focus();
};

const selectCountry = (option: CountryPhoneOption): void => {
  countryOption.value = option;
  inputValue.value = '';
  closeCountrySelection();
};
</script>

<template>
  <div
    class="phone-input"
    :class="finalClasses"
  >
    <div class="phone-input__country input-country">
      <button
        class="input-country__selection country-selection"
        role="button"
        type="button"
        tabindex="-1"
        @click.prevent="showCountrySelection = !showCountrySelection"
      >
        <span class="country-selection__flag">
          <CountryFlag :country="countryOption.code" />
        </span>
        <Typography
          variant="body-small"
          color="gray-darker"
        >
          {{ countryOption.prefix }}
        </Typography>
        <Typography
          variant="body-small"
          color="gray-darker"
          class="country-selection__arrow"
        >
          ▼
        </Typography>
      </button>
      <ul
        v-if="showCountrySelection"
        v-click-away="closeCountrySelection"
        class="input-country__dropdown country-dropdown"
      >
        <li
          v-for="option in countriesOptions"
          :key="option.code"
          role="option"
          class="country-dropdown__item"
          tabindex="-1"
          @click="selectCountry(option)"
        >
          <CountryFlag :country="option.code.toLowerCase()" />
          <span>{{ option.name }}</span>
          <span>{{ option.prefix }}</span>
        </li>
      </ul>
    </div>
    <input
      ref="inputElement"
      v-model="inputValue"
      class="phone-input__field input-field"
      :name="inputName"
      :placeholder="placeholder"
      :disabled="disabled"
      @focus="emit('focus')"
      @blur="emit('blur')"
    >
  </div>
</template>
